import { useEffect, useRef, useState } from "react";
import { FileDropZone } from "@/components/common/FileDropZone";
import { useApolloClient, useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { Button } from "@/components/design/Button";
import { ExtractionDocumentPicker } from "./ExtractionDocumentPicker";
import { ExtractionDialogFrame } from "./ExtractionDialogFrame";
import {
  CreateDocumentDocument,
  MyselfExpertiseDataDocument,
  PowersDataDocument,
  StartDocumentsExtractionDocument,
} from "@/graphql/generated/operations";
import { awaitDocumentsExtraction } from "@/lib/await-extraction";
import { throwForValidationResult, userFacingGraphqlError } from "@/lib/graphql-error";
import { ELEMENT_CODES, elementDisplayName, elementNameId } from "@/lib/elements";
import { levelFromPercent } from "@/lib/skill-levels";
import { MAX_DOCUMENT_UPLOAD_BYTES, MAX_DOCUMENTS_PER_RUN } from "@/lib/upload-limits";
import { ANALYZABLE_ACCEPT, isAnalyzableDocument } from "@/lib/analyzable-documents";

// genesis startDocumentsExtraction runs LLM extraction (Document Extraction
// service) over documents and writes the results onto the person's profile.
// This dialog covers the two silently-persisting pipelines — Power- (extractPowers)
// and Hardskill-/Fachwissen- (extractSkills) analysis — parameterised by `flags`.
// CV/profile analysis is a review-first flow and lives in ProfileExtractionDialog
// (the extractProfileFromDocument mutation), NOT here — genesis does not process a
// profile extraction through this path.
//
// Transport (genesis #113): the mutation returns immediately with a QUEUED job;
// progress and the final result stream over the documentsExtractionEvents
// subscription. This replaces the old blocking processDocuments await, which
// the BFF severed at GENESIS_TIMEOUT_MS (~30s) well before a minutes-long power
// extraction finished. On a dropped socket the outcome is recovered by polling
// the job status, with the completion notification as the durable fallback.

export interface ExtractionFlags {
  extractPowers?: boolean;
  extractSkills?: boolean;
}

export interface ExtractionDoc {
  id: string;
  name: string;
  fileName?: string;
  // Folder the document lives in (null = "Ohne Ordner").
  groupId?: string | null;
  // Drives the newest-first order of the list.
  createdAt?: string | null;
}

interface PendingFile {
  fileName: string;
  mimeType: string;
  content: string; // base64 (no data-URL prefix)
}

// `documents` is the run's whole accounting, so every number the panel shows is counted from it
// rather than read from a total.
// @external-adr GENESIS-0039-an-extraction-run-is-accounted-for-once-per-document
const DOCUMENT_OUTCOMES = [
  "analysed",
  "already_analysed",
  "nothing_found",
  "unreadable",
  "failed",
] as const;

type DocumentOutcome = (typeof DOCUMENT_OUTCOMES)[number];

/** `unreadable` is the user's file to fix, `failed` is ours to retry — both read as "not analysed". */
const UNUSABLE_OUTCOMES: ReadonlySet<DocumentOutcome> = new Set<DocumentOutcome>(["unreadable", "failed"]);

const OUTCOME_LABELS: Record<DocumentOutcome, { id: string; defaultMessage: string }> = {
  analysed: { id: "save.extraction.outcome.analysed", defaultMessage: "Analysed" },
  already_analysed: { id: "save.extraction.outcome.alreadyAnalysed", defaultMessage: "Already analysed" },
  nothing_found: { id: "save.extraction.outcome.nothingFound", defaultMessage: "Analysed, nothing found" },
  unreadable: { id: "save.extraction.outcome.unreadable", defaultMessage: "No readable text" },
  failed: { id: "save.extraction.outcome.failed", defaultMessage: "Analysis failed" },
};

const OUTCOME_COUNT_LABELS: Record<DocumentOutcome, { id: string; defaultMessage: string }> = {
  analysed: { id: "save.extraction.count.analysed", defaultMessage: "{count} analysed" },
  already_analysed: { id: "save.extraction.count.alreadyAnalysed", defaultMessage: "{count} already analysed" },
  nothing_found: { id: "save.extraction.count.nothingFound", defaultMessage: "{count} nothing found" },
  unreadable: { id: "save.extraction.count.unreadable", defaultMessage: "{count} unreadable" },
  failed: { id: "save.extraction.count.failed", defaultMessage: "{count} failed" },
};

/**
 * An unknown status is reported as `failed` rather than rendered as-is. The wire value is an
 * untranslated server string, and showing one in a German dialog is the defect this panel fixes.
 */
function toOutcome(status: string | null | undefined): DocumentOutcome {
  const normalised = status?.toLowerCase() ?? "";
  return (DOCUMENT_OUTCOMES as readonly string[]).includes(normalised)
    ? (normalised as DocumentOutcome)
    : "failed";
}

interface ProcessResult {
  // Read only when `documents` is empty: on the poll-recovered path the payload is lost and this is
  // the sole surviving signal. Never rendered.
  status: string;
  documents: readonly { ref: string; status: string; displayName?: string }[];
}

function readBase64(file: File): Promise<PendingFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result);
      const comma = s.indexOf(",");
      resolve({
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        content: comma >= 0 ? s.slice(comma + 1) : s,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ExtractionDialog({
  title,
  flags,
  docs,
  groups = [],
  docsLoading = false,
  docsLoadError = false,
  personId,
  onClose,
  onDone,
}: {
  title: string;
  flags: ExtractionFlags;
  docs: readonly ExtractionDoc[];
  groups?: readonly { id: string; name: string }[];
  // While true the stock is still arriving, so an empty list says "not yet".
  docsLoading?: boolean;
  docsLoadError?: boolean;
  // Needed to persist newly uploaded files as Documents before analysing them.
  personId: string | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  const intl = useIntl();
  const apollo = useApolloClient();
  const [startDocumentsExtraction] = useMutation(StartDocumentsExtractionDocument);
  const [createDocument] = useMutation(CreateDocumentDocument);
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // True once the background job has been accepted (a job id came back). From
  // that point the extraction is detached server-side, so the user may close the
  // dialog and keep working — the completion notification is the durable signal.
  const [jobAccepted, setJobAccepted] = useState(false);
  // Aborts the background event watch when the dialog is closed mid-run, so we
  // don't leave a dangling subscription/poll after the user walks away.
  const abortRef = useRef<AbortController | null>(null);
  // Everything this run has claimed: documents ticked in the list, files already read into
  // `pending`, and files a handler is still reading. One counter for both halves, because both move
  // asynchronously — a drop decides against a render-time `selectedIds`, a tick against a
  // render-time `pending` — so each would overshoot while the other is still in flight. The ref is
  // only ever touched from event handlers, never from inside a state updater.
  const spentRef = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProcessResult | null>(null);
  // Delta workaround: processDocuments returns only counts, not which entries
  // were added. We snapshot Fachwissen (expertises) before/after and show the
  // newly-added ones. null = not computed (e.g. powers analysis or lookup failed).
  const [newSkills, setNewSkills] = useState<{ skill: string; level: string | null }[] | null>(
    null,
  );
  // Scores carry forward, so an element missing from the delta may simply not
  // have been re-scored rather than having been re-scored to the same value.
  const [changedPowers, setChangedPowers] = useState<{ element: string; score: number }[] | null>(
    null,
  );
  // How many of the 30 powers still have no score at all. A state fact, not a
  // report on this run — @adr SPARK-0045-a-power-with-no-indication-is-counted-not-explained@1.
  const [unscoredPowers, setUnscoredPowers] = useState<number | null>(null);
  // Whether freshly uploaded files are persisted as Documents. Off → the file
  // is only sent for analysis (ephemeral, not stored).
  const [persistUploads, setPersistUploads] = useState(true);

  // Stop watching the job if the dialog is unmounted (user closed it mid-run).
  useEffect(() => () => abortRef.current?.abort(), []);

  // Names already present in Documents (case-insensitive) - used to warn about
  // duplicates the user may have overlooked, and to reuse the existing doc.
  const existingNames = new Set(docs.map((d) => d.name.toLowerCase()));

  const total = pending.length + selectedIds.size;
  // Slots left in this run, for the selection controls. Files go through `spentRef` instead: this
  // value is render-time, and a second drop starting mid-read would see it unchanged.
  const room = Math.max(0, MAX_DOCUMENTS_PER_RUN - total);

  const sayTooMany = () =>
    setError(
      intl.formatMessage(
        {
          id: "save.extraction.tooMany",
          defaultMessage: "You can analyse at most {max} documents at once.",
        },
        { max: MAX_DOCUMENTS_PER_RUN },
      ),
    );

  async function addFiles(files: FileList | null) {
    if (!files) return;
    setError(null);
    const accepted: PendingFile[] = [];
    let claimed = 0;
    try {
      for (const f of Array.from(files)) {
        if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
          sayTooMany();
          break;
        }
        if (!isAnalyzableDocument(f.name, f.type)) {
          setError(
            intl.formatMessage(
              {
                id: "save.extraction.unsupportedType",
                defaultMessage:
                  "“{name}” can’t be analysed. Supported: PDF, Word, Excel and images.",
              },
              { name: f.name },
            ),
          );
          continue;
        }
        if (f.size > MAX_DOCUMENT_UPLOAD_BYTES) {
          setError(
            intl.formatMessage(
              {
                id: "save.extraction.fileTooLarge",
                defaultMessage: "“{name}” is too large (max. 10 MB per file).",
              },
              { name: f.name },
            ),
          );
          continue;
        }
        spentRef.current += 1;
        claimed += 1;
        accepted.push(await readBase64(f));
      }
    } finally {
      // A read that threw never becomes a pending file, so its slot goes back.
      spentRef.current -= claimed - accepted.length;
    }
    if (accepted.length) setPending((p) => [...p, ...accepted]);
  }

  const toggleDoc = (id: string) => {
    if (selectedIds.has(id)) {
      spentRef.current -= 1;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
      // Said out loud rather than swallowed: the checkbox can still look available while a drop is
      // mid-read, because `room` is render-derived and `pending` has not committed yet.
      sayTooMany();
      return;
    }
    spentRef.current += 1;
    setSelectedIds((prev) => new Set(prev).add(id));
  };

  const toggleMany = (ids: readonly string[], select: boolean) => {
    const next = new Set(selectedIds);
    for (const id of ids) {
      if (!select) {
        if (next.delete(id)) spentRef.current -= 1;
        continue;
      }
      if (next.has(id)) continue;
      // Fills the remaining slots rather than refusing the whole group: picking 3 of 5 is what the
      // user wanted from "select all" once the run is nearly full.
      if (spentRef.current >= MAX_DOCUMENTS_PER_RUN) {
        sayTooMany();
        break;
      }
      next.add(id);
      spentRef.current += 1;
    }
    setSelectedIds(next);
  };

  // Fachwissen entries: the displayable text is `description` (`position` is a
  // numeric sort order, `level` the proficiency stored as a percent).
  async function fetchExpertises(): Promise<
    { id: string; description: string | null; level: number | null }[]
  > {
    const r = await apollo.query({
      query: MyselfExpertiseDataDocument,
      fetchPolicy: "network-only",
    });
    return (r.data?.myself?.expertises ?? []).map((e) => ({
      id: e.id,
      description: e.description ?? null,
      level: e.level ?? null,
    }));
  }

  // Smart-analysis (power) results the extraction service fills, keyed by the
  // power element with a score.
  async function fetchSmartAnalysis(): Promise<{ element: string; score: number }[]> {
    const r = await apollo.query({ query: PowersDataDocument, fetchPolicy: "network-only" });
    return (r.data?.smartAnalysisResults ?? []).map((s) => ({
      element: String(s.element),
      score: s.score,
    }));
  }


  async function run() {
    if (busy || total === 0) return;
    setBusy(true);
    setError(null);
    setJobAccepted(false);
    setNewSkills(null);
    setChangedPowers(null);
    setUnscoredPowers(null);
    try {
      // Snapshot Fachwissen / Powers before analysis so we can show what got
      // added (processDocuments only returns counts). Best-effort: never block.
      let beforeSkillIds: Set<string> | null = null;
      if (flags.extractSkills) {
        try {
          beforeSkillIds = new Set((await fetchExpertises()).map((e) => e.id));
        } catch {
          beforeSkillIds = null;
        }
      }
      let beforePowers: Map<string, number> | null = null;
      if (flags.extractPowers) {
        try {
          beforePowers = new Map((await fetchSmartAnalysis()).map((s) => [s.element, s.score]));
        } catch {
          beforePowers = null;
        }
      }
      const documents: { fileName: string; documentId?: string; content?: string }[] = [];
      for (const f of pending) {
        // Reuse rather than re-upload: a file whose name is already in Documents is analysed from
        // the stored document instead. This is a deliberate choice to avoid uploading the same bytes
        // twice, NOT a consequence of a uniqueness rule - genesis has no unique constraint on
        // document_name, so it is a heuristic: with several same-named documents this picks the
        // first match arbitrarily.
        const existing = docs.find((d) => d.name.toLowerCase() === f.fileName.toLowerCase());
        if (existing) {
          documents.push({ fileName: existing.name, documentId: existing.id });
          continue;
        }
        if (!persistUploads) {
          // Analyse-only: send the content inline, don't store a Document.
          // Inline (ephemeral) analyses yield scores but no stored evidence -
          // genesis has no document to attach the quotes to.
          documents.push({ fileName: f.fileName, content: f.content });
          continue;
        }
        // Persist as a new Document (normal upload → no "Lebenslauf" tag →
        // source "Dateiupload"), then analyse it by id so evidence rows can
        // attach to the document and re-runs are deduplicated.
        if (!personId)
          throw new Error(
            intl.formatMessage({
              id: "save.extraction.notSignedIn",
              defaultMessage: "Not signed in - upload not possible.",
            }),
          );
        const cr = await createDocument({
          variables: {
            input: {
              data: {
                name: f.fileName,
                person: { id: personId },
                tags: [],
                // fileName is a top-level Document field; CreateFileDataInput
                // (content) only accepts mimeType + content.
                fileName: f.fileName,
                content: { mimeType: f.mimeType, content: f.content },
              },
              dryRun: false,
            },
          },
        }).catch((e: unknown) => {
          throw new Error(
            userFacingGraphqlError(
              e,
              intl.formatMessage({
                id: "save.extraction.uploadFailedDuplicate",
                defaultMessage:
                  "Upload failed. A document with this name probably already exists.",
              }),
            ),
          );
        });
        const cout = cr.data?.createDocument;
        if (cout?.__typename !== "CreateDocumentResult") {
          throwForValidationResult(
            cout,
            intl.formatMessage({
              id: "save.extraction.uploadFailedDuplicate",
              defaultMessage:
                "Upload failed. A document with this name probably already exists.",
            }),
          );
        }
        documents.push({
          fileName: cout.result.fileName ?? cout.result.name ?? f.fileName,
          documentId: cout.result.id,
        });
      }
      for (const d of docs.filter((d) => selectedIds.has(d.id))) {
        documents.push({ fileName: d.name, documentId: d.id });
      }
      // The same document can arrive twice (a dropped file that already
      // existed + its checkbox selection); send each one once.
      const seen = new Set<string>();
      const uniqueDocuments = documents.filter((d) => {
        const key = d.documentId ?? d.fileName.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Fire the job: returns immediately with a QUEUED handle. The blocking
      // wait now lives on the subscription, not the HTTP request.
      const started = await startDocumentsExtraction({
        variables: {
          input: {
            documents: uniqueDocuments,
            extractPowers: Boolean(flags.extractPowers),
            // Required by the schema but not implemented on this path; profile
            // extraction is the separate extractProfileFromDocument flow.
            extractProfile: false,
            extractSkills: Boolean(flags.extractSkills),
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      }).catch((e: unknown) => {
        throw new Error(
          intl.formatMessage(
            {
              id: "save.extraction.analysisFailedWithReason",
              defaultMessage: "Analysis failed ({reason}).",
            },
            { reason: userErrorMessage(intl, e) },
          ),
          { cause: e },
        );
      });
      const jobId = started.data?.startDocumentsExtraction?.id;
      if (!jobId) {
        throw new Error(
          intl.formatMessage({
            id: "save.extraction.analysisFailed",
            defaultMessage: "Analysis failed.",
          }),
        );
      }

      // Job accepted and now detached server-side: from here the user is free to
      // close the dialog and keep working (Close is enabled below).
      setJobAccepted(true);

      // Watch the job's events for the live result (status poll is the
      // dropped-socket fallback inside awaitDocumentsExtraction). Aborted if the
      // dialog is closed mid-run.
      const controller = new AbortController();
      abortRef.current = controller;
      const outcome = await awaitDocumentsExtraction(apollo, jobId, { signal: controller.signal });

      // Dialog was closed while the job ran — nothing to render.
      if (outcome.kind === "cancelled") {
        return;
      }

      if (outcome.kind === "timeout") {
        // The job is still running server-side; its result arrives as a durable
        // notification that deep-links back here.
        throw new Error(
          intl.formatMessage({
            id: "save.extraction.stillRunning",
            defaultMessage:
              "The analysis is taking a while and keeps running in the background. You’ll get a notification when it’s done.",
          }),
        );
      }

      const out = outcome.result;
      if (outcome.kind === "failed" && !out) {
        throw new Error(
          outcome.message ??
            intl.formatMessage({
              id: "save.extraction.analysisFailed",
              defaultMessage: "Analysis failed.",
            }),
        );
      }

      // On the poll-recovered path the result payload is absent; synthesise a
      // minimal result so the outcome (and the computed deltas below) still show.
      const resolved: ProcessResult = out
        ? {
            ...(out as unknown as ProcessResult),
            documents: out.documents.map((d, index) => ({
              ...d,
              displayName: uniqueDocuments[index]?.fileName,
            })),
          }
        : {
            status: outcome.kind === "completed" ? "success" : "error",
            documents: [],
          };
      setResult(resolved);
      // Compute the Fachwissen delta (new entries, with proficiency) for display.
      if (flags.extractSkills && beforeSkillIds) {
        try {
          const after = await fetchExpertises();
          setNewSkills(
            after
              .filter((e) => !beforeSkillIds.has(e.id))
              .map((e) => ({
                skill:
                  e.description ??
                  intl.formatMessage({
                    id: "save.extraction.expertiseFallback",
                    defaultMessage: "Expertise",
                  }),
                level: (() => {
                  const level = levelFromPercent(e.level);
                  return level ? intl.formatMessage({ id: level.nameId, defaultMessage: level.name }) : null;
                })(),
              })),
          );
        } catch {
          // delta is a nice-to-have; ignore lookup failures
        }
      }
      if (flags.extractPowers) {
        try {
          const after = await fetchSmartAnalysis();
          const scored = new Set(after.map((s) => s.element));
          setUnscoredPowers(Object.keys(ELEMENT_CODES).filter((e) => !scored.has(e)).length);
          if (beforePowers) {
            const before = beforePowers;
            setChangedPowers(after.filter((s) => before.get(s.element) !== s.score));
          }
        } catch {
          // delta and count are nice-to-haves; ignore lookup failures
        }
      }
      onDone?.();
    } catch (e) {
      // Errors wrapped by the steps above (cause set) carry labeled product
      // copy with an already-sanitized reason; anything else is raw and goes
      // through the @adr SPARK-0010-no-raw-server-errors-in-ui@1 mapper.
      setError(
        e instanceof Error && e.cause !== undefined ? e.message : userErrorMessage(intl, e),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <ExtractionDialogFrame id="analysis" title={title} onClose={onClose} preventClose={busy && !jobAccepted}
      dataGraphql="StartDocumentsExtraction, DocumentsExtractionEvents, CreateDocument, MyselfExpertises, PowersData"
      footer={result ? (
          <div className="flex justify-end border-t border-ink/10 px-5 py-3">
            <Button id="analysis-done" variant="primary" size="compact" onClick={onClose}>
              <FormattedMessage id="save.extraction.done" defaultMessage="Done" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 border-t border-ink/10 px-5 py-3">
            <span id="analysis-selection" className="text-smycles-label text-ink-muted">
              {jobAccepted ? (
                <FormattedMessage
                  id="save.extraction.runningInBackground"
                  defaultMessage="Running in the background - you can close this and keep working. You’ll be notified when it’s done."
                />
              ) : total > 0 ? (
                <FormattedMessage
                  id="save.extraction.selectedCount"
                  defaultMessage="{total, plural, one {# document selected} other {# documents selected}}"
                  values={{ total }}
                />
              ) : (
                <FormattedMessage
                  id="save.extraction.nothingSelected"
                  defaultMessage="Nothing selected"
                />
              )}
            </span>
            <Button
              id="analysis-start"
              variant="primary"
              onClick={run}
              // `total > MAX` is unreachable through the UI; it is the backstop for a selection
              // path that ever forgets to check, since genesis refuses such a run anyway.
              disabled={busy || total === 0 || total > MAX_DOCUMENTS_PER_RUN}
              aria-busy={busy}
              // Fixed min-width so the label swap doesn't change the button
              // size mid-run; the spinner lives INSIDE the pill.
              className="inline-flex min-w-[10rem] items-center justify-center gap-2 px-4 py-1.5 text-smycles-label disabled:opacity-40"
            >
              {busy ? (
                <>
                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 animate-spin rounded-full border border-page/40 border-t-page"
                  />
                  <FormattedMessage
                    id="save.extraction.analysing"
                    defaultMessage="Analysing …"
                  />
                </>
              ) : (
                <FormattedMessage
                  id="save.extraction.startAnalysis"
                  defaultMessage="Start analysis"
                />
              )}
            </Button>
          </div>
        )}>
          {result ? (
            <ResultView
              result={result}
              docs={docs}
              newSkills={newSkills}
              changedPowers={changedPowers}
              unscoredPowers={unscoredPowers}
            />
          ) : (
            <>
              <div>
                <div className="smycles-field-label mb-1.5 text-ink-muted">
                  <FormattedMessage id="save.extraction.newFile" defaultMessage="New file" />
                </div>
                <FileDropZone
                  inputId="analysis-file"
                  variant="zone"
                  clickToOpen
                  accept={ANALYZABLE_ACCEPT}
                  onFiles={(fl) => void addFiles(fl)}
                  label={
                    <FormattedMessage
                      id="save.extraction.dropZone"
                      defaultMessage="Drag a file here or click to upload"
                    />
                  }
                  hint={
                    <FormattedMessage
                      id="save.extraction.dropZoneHint"
                      defaultMessage="PDF, Word, Excel, images · max. 10 MB per file"
                    />
                  }
                />

                {/* bd-requirements#16. Per pipeline, because the two disagree
                    about what a good document is: only Powers tries to attribute
                    evidence to the person at all, and a bare skill list is
                    worthless there while it is prime evidence for Fachwissen
                    (skill-extraction.yaml). The flags are mutually exclusive at
                    every call site (use-service-actions.tsx), so extractPowers
                    alone decides which pair is shown.
                    "Attribute", not "skip": HintMiningStage sends EVERY page to
                    the model regardless: the person check is a prompt line
                    (hint-mining-pro.yaml) plus a second per-quote judgement in
                    evidence-filter.yaml. Nothing deterministic gates on the
                    name, so the copy must not promise that it does. */}
                <div className="smycles-hint mt-4">
                  {/* Eyebrow rung + body per the Static Hinweis recipe in
                      UI_CONVENTIONS; the topic goes in the eyebrow slot so the
                      guidance stops competing with "New file" / "From Documents"
                      at the smycles-field-label rung, which read as three peer sections. */}
                  <div className="text-smycles-micro tracking-[0.2em] text-ink-accent">
                    <FormattedMessage
                      id="save.extraction.guideTitle"
                      defaultMessage="Which documents work best"
                    />
                  </div>
                  <ul className="mt-1 smycles-field-meta list-disc space-y-1 pl-4 leading-relaxed text-ink marker:text-ink-muted">
                    <li>
                      {flags.extractPowers ? (
                        <FormattedMessage
                          id="save.extraction.guidePowersAboutYou"
                          defaultMessage="It is about you. Evidence the analysis cannot attribute to you is discarded, and whatever is found is written to your profile."
                        />
                      ) : (
                        <FormattedMessage
                          id="save.extraction.guideSkillsAboutYou"
                          defaultMessage="It is about you. Nothing checks that, so another person's document writes their skills onto your profile."
                        />
                      )}
                    </li>
                    <li>
                      {flags.extractPowers ? (
                        <FormattedMessage
                          id="save.extraction.guidePowersConcrete"
                          defaultMessage="It describes how you work: references, appraisals, feedback, project and role descriptions. A job title or a plain skills list provides no usable evidence."
                        />
                      ) : (
                        <FormattedMessage
                          id="save.extraction.guideSkillsConcrete"
                          defaultMessage="It names tools, technologies, methods or fields: CVs, certificates, project documentation. A plain skills list is valid evidence on its own."
                        />
                      )}
                    </li>
                  </ul>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3">
                  <span className="text-smycles-label text-ink-muted">
                    <FormattedMessage
                      id="save.extraction.persistUploads"
                      defaultMessage="Save uploaded files in Documents"
                    />
                  </span>
                  <button
                    id="analysis-persist"
                    type="button"
                    role="switch"
                    aria-checked={persistUploads}
                    aria-label={intl.formatMessage({
                      id: "save.extraction.persistUploads",
                      defaultMessage: "Save uploaded files in Documents",
                    })}
                    onClick={() => setPersistUploads((v) => !v)}
                    className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${
                      persistUploads ? "bg-accent" : "bg-ink/25"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-surface shadow transition ${
                        persistUploads ? "translate-x-[18px]" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </div>

                {pending.length > 0 ? (
                  <div className="mt-2 space-y-1">
                    {pending.map((f, i) => {
                      const duplicate = existingNames.has(f.fileName.toLowerCase());
                      return (
                        <div
                          key={`${f.fileName}-${i}`}
                          className="box-flat bg-page px-3 py-1.5 text-smycles-meta"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate">{f.fileName}</span>
                            <button
                              type="button"
                              onClick={() => {
                                spentRef.current -= 1;
                                setPending((p) => p.filter((_, j) => j !== i));
                              }}
                              aria-label={intl.formatMessage({
                                id: "common.remove",
                                defaultMessage: "Remove",
                              })}
                              className="shrink-0 text-ink-muted"
                            >
                              ×{/* i18n-allow: close glyph, not copy */}
                            </button>
                          </div>
                          {duplicate ? (
                            <div className="mt-0.5 text-smycles-micro text-ink-attention">
                              <FormattedMessage
                                id="save.extraction.duplicateWarning"
                                defaultMessage="Already exists in Documents - the stored document is analysed, no re-upload."
                              />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <ExtractionDocumentPicker docs={docs} groups={groups} docsLoading={docsLoading} docsLoadError={docsLoadError}
                selectedIds={selectedIds} room={room} onToggle={toggleDoc} onToggleMany={toggleMany} />

              {error ? <div id="analysis-error" className="text-smycles-label text-danger">{error}</div> : null}
            </>
          )}
    </ExtractionDialogFrame>
  );
}

function ResultView({
  result,
  docs,
  newSkills,
  changedPowers,
  unscoredPowers,
}: {
  result: ProcessResult;
  docs: readonly ExtractionDoc[];
  newSkills?: { skill: string; level: string | null }[] | null;
  changedPowers?: { element: string; score: number }[] | null;
  unscoredPowers?: number | null;
}) {
  const intl = useIntl();
  // documentId refs can be mapped from `docs`; inline refs get displayName set
  // from the request order before the result is stored.
  const nameByRef = new Map(docs.map((d) => [d.id, d.name]));

  const outcomes = result.documents.map((d) => toOutcome(d.status));
  const unusable = outcomes.filter((outcome) => UNUSABLE_OUTCOMES.has(outcome)).length;
  // The headline stays "abgeschlossen" while anything worked, because the job is detached: people
  // arrive here from a notification minutes later, and whether it finished is what they came to see.
  // Only a run where nothing survived turns it red. With no documents at all — the poll-recovered
  // path, where the payload was lost — the run-level status is all that is left to go on.
  const failedRun =
    outcomes.length === 0
      ? !["ok", "success"].includes(result.status?.toLowerCase() ?? "")
      : unusable === outcomes.length;
  const nothingContributed =
    outcomes.includes("nothing_found") && !outcomes.includes("analysed");
  const breakdown = DOCUMENT_OUTCOMES.map((outcome) => ({
    outcome,
    count: outcomes.filter((o) => o === outcome).length,
  })).filter((bucket) => bucket.count > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Kennung je Zustand: die Zeile steht in BEIDEN Fällen, nur ihr Text
            unterscheidet sie. Ein Test soll den Ausgang nicht am Wort ablesen. */}
        <span
          id={failedRun ? "analysis-statusfailed" : "analysis-statusok"}
          className={`text-smycles-meta font-medium ${failedRun ? "text-danger" : "text-ink"}`}
        >
          {failedRun ? (
            <FormattedMessage
              id="save.extraction.analysisFailed"
              defaultMessage="Analysis failed."
            />
          ) : (
            <FormattedMessage
              id="save.extraction.analysisComplete"
              defaultMessage="Analysis complete."
            />
          )}
        </span>
        {/* The canonical chip fill with amber text: the run finished, some documents just did not
            come through — "needs a look", not a second failure signal. */}
        {unusable > 0 && !failedRun ? (
          <span className="inline-flex items-center rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-label text-ink-attention">
            <FormattedMessage
              id="save.extraction.notAnalysedChip"
              defaultMessage="{count} not analysed"
              values={{ count: unusable }}
            />
          </span>
        ) : null}
      </div>
      {breakdown.length > 0 ? (
        <div className="box-flat bg-page px-3 py-2 text-smycles-meta text-ink-muted">
          {breakdown
            .map((bucket) => intl.formatMessage(OUTCOME_COUNT_LABELS[bucket.outcome], { count: bucket.count }))
            .join(" · ")}
        </div>
      ) : null}
      {!failedRun && newSkills ? (
        <div id="analysis-new-skills">
          <div className="smycles-field-label mb-1 text-ink-muted">
            <FormattedMessage
              id="save.extraction.newSkillsHeading"
              defaultMessage="Newly detected expertise{count, plural, =0 {} other { (#)}}"
              values={{ count: newSkills.length }}
            />
          </div>
          {newSkills.length === 0 ? (
            <div className="text-smycles-label text-ink-muted">
              <FormattedMessage
                id="save.extraction.noNewSkills"
                defaultMessage="No new entries - everything was probably already in your expertise."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {newSkills.map((s, i) => (
                <span
                  key={`${s.skill}-${i}`}
                  id={`analysis-skill-${s.skill}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-smycles-label text-ink"
                >
                  {s.skill}
                  {s.level ? <span className="text-ink-muted">· {s.level}</span> : null}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {!failedRun && changedPowers ? (
        <div id="analysis-new-powers">
          <div className="smycles-field-label mb-1 text-ink-muted">
            <FormattedMessage
              id="save.extraction.powersUpdatedHeading"
              defaultMessage="Powers updated{count, plural, =0 {} other { (#)}}"
              values={{ count: changedPowers.length }}
            />
          </div>
          {changedPowers.length === 0 ? (
            <div id="analysis-no-new-powers" className="text-smycles-label text-ink-muted">
              <FormattedMessage
                id="save.extraction.noPowerChanges"
                defaultMessage="No changes – no power value moved."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {changedPowers.map((p, i) => (
                <span
                  key={`${p.element}-${i}`}
                  id={`analysis-power-${p.element}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-smycles-label text-ink"
                >
                  {intl.formatMessage({
                    id: elementNameId(p.element),
                    defaultMessage: elementDisplayName(p.element),
                  })}
                  <span className="text-ink-muted">· {p.score}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {!failedRun && unscoredPowers != null && unscoredPowers > 0 ? (
        <div className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="save.extraction.powersNoIndication"
            defaultMessage="{count, plural, one {# power with no indication.} other {# powers with no indication.}}"
            values={{ count: unscoredPowers }}
          />
        </div>
      ) : null}
      {!failedRun && nothingContributed ? (
        <div className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="save.extraction.nothingFoundHint"
            defaultMessage="Try references, appraisals or feedback – text that describes how you work."
          />
        </div>
      ) : null}
      {result.documents.length > 0 ? (
        <div className="space-y-1.5">
          {result.documents.map((d, i) => {
            const outcome = toOutcome(d.status);
            return (
              <div key={`${d.ref}-${i}`} className="flex items-center justify-between gap-2 text-smycles-label text-ink">
                <span className="truncate">{d.displayName ?? nameByRef.get(d.ref) ?? d.ref}</span>
                <span id={`analysis-docoutcome-${outcome}-${d.ref}`} className="shrink-0 text-ink-muted">
                  {intl.formatMessage(OUTCOME_LABELS[outcome])}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
